import { createUseStyles } from 'react-jss'
import { lightGrey, trafficRedDark, transparent, black, mediumGrey } from '../../styles/const/colors'
import { defaultBorderRadius, transitionShort, zIndex10 } from '../../styles/const/common'
import { phonesDown, tabletDown } from '../../styles/const/breakpoints'
import { colorsValue, backgroundColorsValue } from '../Chip/styles'
import { fontWeightBold } from '../../styles/typography'
import { placeholderColor } from '../../styles/mixins/common'
import { isWhite } from '../../helpers/common'
import { SELECT_OPTION_FOCUSED_COLOR } from '../../styles/themes/getDefaultTheme'

const menuHeight = 300

export const selectValueSize = 14
export const selectValueSizeMobile = 12
export const leftSpaceSize = 13
export const selectValueContainerHeight = 21

export const useStyles = createUseStyles({
  select: {
    position: 'relative',
    cursor: props => (props && props.isDisabled ? 'not-allowed' : 'pointer'),
    // remove tap outline color on mobile devices
    '-webkit-tap-highlight-color': 'transparent'
  },
  valueContainer: {
    display: 'flex',
    flex: 1,
    cursor: 'pointer',
    width: props => props && props.isIconSelect && 0,
    opacity: props => props && props.isIconSelect && 0,
    maxWidth: props => (props && (props.showIndicator || props.showClearInputIcon) ? '83%' : '100%')
  },
  menuContainer: {
    height: 315,
    minWidth: props => (props && props.isIconSelect ? '230px' : 'calc(100% + 4px)'),
    maxWidth: 230,
    overflow: 'hidden',
    position: 'absolute',
    top: '100%',
    right: -2,
    paddingLeft: 3,
    paddingRight: 3,
    zIndex: zIndex10,
    cursor: 'pointer'
  },
  indicatorsWrapper: {
    display: 'flex',
    alignItems: 'center',
    top: '-10px'
  },
  clearInputButton: {
    marginRight: 3
  },
  clearInputIcon: {
    fontSize: [10, '!important'],
    color: mediumGrey,

    '&:hover': {
      cursor: 'pointer'
    }
  },
  menu: {
    animation: '$fadeIn 0.2s ease-out',
    top: '0 !important',
    position: 'static  !important',
    boxShadow: 'none !important',
    overflow: 'hidden',
    border: {
      width: 1,
      style: 'solid',
      color: lightGrey,
      radius: defaultBorderRadius
    }
  },
  '@keyframes fadeIn': {
    '0%': {
      transform: 'translateY(-100px)'
    },
    '100%': {
      transform: 'translateY(0)'
    }
  }
})

const getActiveSelectBorderColor = (noBorder, theme) => {
  if (noBorder) {
    return transparent
  } else if (isWhite(theme.brandPrimary)) {
    // check if theme.brandPrimary is white - if so, use lightGrey
    return lightGrey
  } else {
    return theme.brandPrimary
  }
}

// pseudoDisable/pseudoOpened is used for representing only UI layer of Select
export function getSelectStyles(props) {
  const { theme } = props

  const sharedStyles = {
    container: (provided, { isDisabled }) => ({
      ...provided,
      pointerEvents: props.pseudoDisabled || isDisabled ? 'none' : 'auto'
    }),
    control: (provided, { menuIsOpen, isDisabled }) => ({
      ...provided,
      minHeight: props.isSmall ? 37 : 40,
      maxHeight: props.isSmall ? 37 : 40,
      borderWidth: props.isThickBorder ? 2 : 1,
      borderRadius: defaultBorderRadius,
      borderColor: getBorderColor(props, menuIsOpen, theme),
      boxShadow: 'none',
      cursor: 'pointer',
      // Select could have transparent background
      backgroundColor: props.transparent ? 'transparent' : !isDisabled ? 'white' : lightGrey,
      flexWrap: 'no-wrap',
      maxWidth: '100%',

      ':hover': {
        ...provided[':hover'],
        borderColor: getActiveSelectBorderColor(props.noBorder, theme)
      },
      ':active': {
        ...provided[':active'],
        borderColor: getActiveSelectBorderColor(props.noBorder, theme)
      },
      ':focused': {
        ...provided[':active'],
        borderColor: getActiveSelectBorderColor(props.noBorder, theme)
      },
      [`@media screen and (${phonesDown})`]: {
        minHeight: 34
      }
    }),
    valueContainer: provided => ({
      ...provided,
      paddingTop: 0,
      paddingBottom: 0,
      marginTop: 0,
      paddingLeft: leftSpaceSize,
      paddingRight: 8,
      cursor: 'pointer'
    }),
    menu: provided => ({
      ...provided,
      marginTop: '2px',
      color: black,
      borderRadius: defaultBorderRadius,
      right: props.menuPosition === 'right' ? 0 : 'unset',
      minWidth: '100%',
      // auto width makes menu container wider then select container if option content doesn't fit
      width: props.iconStyleSelect ? '150px' : 'auto',
      zIndex: zIndex10
    }),
    input: provided => ({
      ...provided,
      margin: 0,
      padding: 0,
      ...(props.singleValueStyles || {}),
      '& input': {
        ...placeholderColor(mediumGrey)
      }
    }),
    singleValue: provided => ({
      ...provided,
      color: black,
      marginLeft: 0,
      ...(props.singleValueStyles || {})
    }),
    dropdownIndicator: provided => ({
      ...provided,
      color: theme.brandPrimary,
      cursor: 'pointer',
      padding: props.isIconSelect ? '8px 9px' : '8px',
      ':hover': {
        ...provided[':hover'],
        color: theme.brandPrimary
      },
      [`@media screen and (${tabletDown})`]: {
        padding: props.isIconSelect ? '8px 13px' : '8px'
      }
    }),
    placeholder: provided => ({
      ...provided,
      margin: 0,
      color: mediumGrey
    }),
    menuPortal: provided => ({
      ...provided,
      zIndex: zIndex10
    }),
    menuList: provided => ({
      ...provided,
      maxHeight: menuHeight,

      '::-webkit-scrollbar': {
        width: '12px'
      },
      '::-webkit-scrollbar-thumb': {
        background: lightGrey,
        borderRadius: '12px',
        border: 'solid 3px white'
      },
      '::-webkit-scrollbar-track': {
        border: 'solid 3px white'
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: lightGrey
      }
    }),
    option: (provided, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...provided,
        fontWeight: isSelected ? fontWeightBold : 'regular',
        backgroundColor: isFocused ? theme.brandPrimaryLight : isSelected ? 'white' : null,
        // color: isFocused ? theme.brandPrimary : black,
        color: isFocused ? theme[SELECT_OPTION_FOCUSED_COLOR] : black,
        cursor: 'pointer',
        ':active': {
          ...provided[':active'],
          backgroundColor: !isDisabled && (isSelected ? 'white' : theme.brandPrimaryLight),
          fontWeight: fontWeightBold
        },
        '& .tag': {
          // data && data.tagColor - huck to avoid app crashing
          ...tagStyles(data && data.tagColor),
          padding: '3px 5px',
          display: 'inline-flex',

          '&:not(:first-of-type)': {
            marginLeft: '5px'
          }
        }
      }
    },
    indicatorsContainer: (provided, { selectProps: { menuIsOpen } }) => ({
      ...provided,
      transition: transitionShort,
      transform: menuIsOpen || props.pseudoOpened ? 'rotate(180deg)' : 'rotate(0deg)',
      '& svg': {
        color: mediumGrey,
        width: 12,
        height: 12
      }
    }),
    indicatorSeparator: provided => ({
      ...provided,
      display: 'none'
    }),
    multiValue: (provided, { data }) => ({
      ...provided,
      ...tagStyles(data && data.tagColor),
      padding: '3px 2px 3px 5px'
    }),
    multiValueRemove: (provided, { data }) => ({
      ...provided,
      background: 'rgba(0,0,0,0) !important',
      cursor: 'pointer',
      color: colorsValue[(data && data.tagColor) || props.tagsColor],

      ':hover': {
        color: theme.brandPrimary
      }
    })
  }

  const tagStyles = color => {
    return {
      backgroundColor: backgroundColorsValue[color || props.tagsColor],
      color: colorsValue[color || props.tagsColor]
    }
  }

  return sharedStyles
}

const getBorderColor = (props, menuIsOpen, theme) => {
  if (props.isDisabled) {
    return lightGrey
  } else if (props.noBorder) {
    return transparent
  } else if (props.hasError) {
    return trafficRedDark
  } else if (menuIsOpen || props.pseudoOpened || props.focused || props.isHovered) {
    return getActiveSelectBorderColor(props.noBorder, theme)
  } else {
    return lightGrey
  }
}
